/**
 * Direction du texte selon la locale — aligné sur la spec RTL (arabe / futures langues RTL).
 */
export type TextDirection = 'ltr' | 'rtl';

/** Locales gérées côté produit : extension possible sans toucher aux consommateurs. */
const RTL_LANGUAGE_CODES = new Set(['ar']);

export type LocaleConfig = {
  code: string;
  label: string;
  direction: TextDirection;
};

/** Référence unique pour documentation / futurs sélecteurs (pas nécessairement exhaustif des variantes régionales). */
export const APP_LOCALE_CONFIGS: LocaleConfig[] = [
  { code: 'fr', label: 'Français', direction: 'ltr' },
  { code: 'en', label: 'English', direction: 'ltr' },
  { code: 'it', label: 'Italiano', direction: 'ltr' },
  { code: 'de', label: 'Deutsch', direction: 'ltr' },
  { code: 'es', label: 'Español', direction: 'ltr' },
  { code: 'uk', label: 'Українська', direction: 'ltr' },
  { code: 'ru', label: 'Русский', direction: 'ltr' },
  { code: 'lb', label: 'Lëtzebuergesch', direction: 'ltr' },
  { code: 'pt', label: 'Português', direction: 'ltr' },
  { code: 'ar', label: 'العربية', direction: 'rtl' },
  { code: 'tr', label: 'Türkçe', direction: 'ltr' },
];

export function getTextDirection(languageCode: string | undefined | null): TextDirection {
  if (languageCode == null || languageCode === '') return 'ltr';
  const code = languageCode.split('-')[0].toLowerCase();
  return RTL_LANGUAGE_CODES.has(code) ? 'rtl' : 'ltr';
}

export function isRtlLocale(languageCode: string | undefined | null): boolean {
  return getTextDirection(languageCode) === 'rtl';
}

/**
 * À fusionner avec le `className` des icônes Lucide `ChevronLeft` / `ChevronRight` /
 * `ChevronsLeft` / `ChevronsRight` pour inverser le sens en RTL (arabe).
 */
export const rtlMirrorHorizontalChevron = 'rtl:scale-x-[-1]';

/** Synchronise `<html lang>` et `<html dir>` (WCAG 3.1.1 + RTL). */
export function applyDocumentLocale(languageCode: string): void {
  if (typeof document === 'undefined') return;
  const code = languageCode.split('-')[0];
  document.documentElement.lang = code;
  document.documentElement.dir = getTextDirection(code);
}
